import { computed, Signal, signal, WritableSignal } from '@angular/core';
import { SortOrder } from '@app/core/enums/sort-order.enum';

export interface IReportTableRow {
  id: string;
}

export class ReportRowSelectionState<T extends IReportTableRow> {
  public selectedIds = signal<Record<string, boolean>>({});

  public totalCount: Signal<number>;
  public selectedCount = computed(() => Object.values(this.selectedIds()).filter(Boolean).length);
  public allSelected: Signal<boolean>;
  public partiallySelected: Signal<boolean>;

  constructor(private readonly rows: () => T[]) {
    this.totalCount = computed(() => this.rows().length);
    this.allSelected = computed(() => this.totalCount() > 0 && this.selectedCount() === this.totalCount());
    this.partiallySelected = computed(() => this.selectedCount() > 0 && this.selectedCount() < this.totalCount());
  }

  public selectAllRows(): void {
    this.selectedIds.set(Object.fromEntries(this.rows().map(row => [row.id, true])));
  }

  public selectRows(ids: string[]): void {
    const idsSet = new Set(ids);

    this.selectedIds.set(Object.fromEntries(this.rows().map(row => [row.id, idsSet.has(row.id)])));
  }

  public toggleAll(): void {
    const shouldSelectAll = !this.allSelected();

    this.selectedIds.set(Object.fromEntries(this.rows().map(row => [row.id, shouldSelectAll])));
  }

  public toggleRow(id: string): void {
    this.selectedIds.update(map => ({ ...map, [id]: !map[id] }));
  }

  public selectedRowIds(): string[] {
    return Object.entries(this.selectedIds())
      .filter(([, selected]) => selected)
      .map(([id]) => id);
  }
}

export type ReportTableSecondarySortFields<F extends string> = Partial<Record<F, F[]>>;

export class ReportTableSortState<T, F extends string> {
  public readonly sortField: WritableSignal<F>;
  public readonly sortOrder: WritableSignal<SortOrder>;

  constructor(
    initialField: F,
    initialOrder: SortOrder,
    private readonly sortValue: (row: T, field: F) => string | number,
    private readonly secondarySortFields: ReportTableSecondarySortFields<F> = {}
  ) {
    this.sortField = signal(initialField);
    this.sortOrder = signal(initialOrder);
  }

  public toggleSort(field: F): void {
    if (this.sortField() === field) {
      this.sortOrder.update(order => (order === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc));
    } else {
      this.sortField.set(field);
      this.sortOrder.set(SortOrder.Asc);
    }
  }

  public sort(rows: T[]): T[] {
    const field = this.sortField();
    const order = this.sortOrder();
    const secondaryFields = this.secondarySortFields[field] ?? [];

    return [...rows].sort((a, b) => {
      const primaryComparison = this.compare(a, b, field, order);
      if (primaryComparison !== 0) return primaryComparison;

      for (const secondaryField of secondaryFields) {
        const secondaryComparison = this.compare(a, b, secondaryField, SortOrder.Asc);
        if (secondaryComparison !== 0) return secondaryComparison;
      }

      return 0;
    });
  }

  private compare(a: T, b: T, field: F, order: SortOrder): number {
    const valueA = this.sortValue(a, field);
    const valueB = this.sortValue(b, field);

    return typeof valueA === 'number' && typeof valueB === 'number'
      ? (valueA - valueB) * order
      : String(valueA).localeCompare(String(valueB)) * order;
  }
}
